import React, { createContext, useContext, useState, ReactNode } from 'react';
import { createLogger } from '../services/logger';

const log = createLogger('LayoutContext');

export type LayoutType = 'original' | 'simplified';

interface LayoutContextType {
  dashboardLayout: LayoutType;
  navigationLayout: LayoutType;
  setDashboardLayout: (layout: LayoutType) => void;
  setNavigationLayout: (layout: LayoutType) => void;
  toggleLayout: () => void;
  isSimplified: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

const STORAGE_KEY = 'suprie_layout_preferences';

// Load layout preferences from localStorage
const loadLayoutPreferences = (): { dashboard: LayoutType; navigation: LayoutType } => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        dashboard: parsed.dashboard || 'original',
        navigation: parsed.navigation || 'original'
      };
    }
  } catch (error) {
    log.warn('Failed to load layout preferences', { error });
  }
  
  return {
    dashboard: 'original',
    navigation: 'original'
  };
};

// Save layout preferences to localStorage
const saveLayoutPreferences = (dashboard: LayoutType, navigation: LayoutType) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dashboard, navigation }));
  } catch (error) {
    log.warn('Failed to save layout preferences', { error, dashboard, navigation });
  }
};

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState(() => loadLayoutPreferences());

  const setDashboardLayout = (layout: LayoutType) => {
    const newPrefs = { ...preferences, dashboard: layout };
    setPreferences(newPrefs);
    saveLayoutPreferences(newPrefs.dashboard, newPrefs.navigation);
  };

  const setNavigationLayout = (layout: LayoutType) => {
    const newPrefs = { ...preferences, navigation: layout };
    setPreferences(newPrefs);
    saveLayoutPreferences(newPrefs.dashboard, newPrefs.navigation);
  };

  const toggleLayout = () => {
    const newLayout: LayoutType = preferences.dashboard === 'simplified' ? 'original' : 'simplified';
    setDashboardLayout(newLayout);
    setNavigationLayout(newLayout);
  };

  const isSimplified = preferences.dashboard === 'simplified' || preferences.navigation === 'simplified';

  const value: LayoutContextType = {
    dashboardLayout: preferences.dashboard,
    navigationLayout: preferences.navigation,
    setDashboardLayout,
    setNavigationLayout,
    toggleLayout,
    isSimplified
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

export default LayoutContext; 